/**
 * Logo URL resolution — pure logoFamily lookup.
 *
 * Every product declares a `logoFamily` in its config (src/data/products/*.js).
 * The LOGO_FAMILIES map below maps family names to logo asset variants.
 * The resolver picks a variant from the background's style MARKERS
 * (`blacklogo`, `whitelogoinverted`, … — see libs/bgMarkers.ts), falling back
 * through the variants the family declares (e.g. blackInverted ?? black ??
 * normal).
 *
 * Markers, not the url: the background bytes are re-hosted under content-hashed
 * keys (`bg/<sha12>.png`) under `bgDerivation`, so reading the marker out of the
 * filename silently resolved every product to its family default (issue #567 —
 * AI Assistant and YouTrack both came up with the plain white JetBrains logo).
 *
 * No more isIDE-as-logo-fallback heuristic. isIDE remains a product
 * taxonomy flag (CSS .ide class, /api/v3/products grouping, etc.) but
 * doesn't drive logo selection — that's logoFamily's job, separately.
 */
import {
  logoAcademyBlack,
  logoAcademyNorm,
  logoIDE,
  logoIDEBlack,
  logoIDEWhite,
  logoNorm,
  logoNormInverted,
  logoNormBlack,
  logoNormBlackInverted,
  logoGameDevBlack,
  logoGameDevNorm,
  logoDotNETBlack,
  logoDotNETNorm,
  logoDataNorm,
  logoDataBlack,
  logoAIBlack,
  logoAINorm,
  logoWhite,
  logoYouTrackBlack,
  logoMinimal,
  logoMinimalColor,
  logoDataWhite,
  logoMarketplaceNorm,
  logoMarketplaceBlack,
  logoWhiteTransparent,
  logoDatabaoOutlined,
  PRODUCT_LOGO_CYCLES,
} from "../data/constants";
import {getProduct, checkIfIDE} from "../data/products/index";

/**
 * Logo families. Keys are valid `logoFamily` values for products.
 *
 * Variant resolution order (in resolveVariant below):
 *   blacklogoinverted → blackInverted ?? black ?? normal
 *   blacklogo         → black ?? normal
 *   whitelogoinverted → inverted ?? white ?? normal
 *   default           → normal
 *
 * Adding a new family: add an entry below + set `logoFamily: "newname"`
 * on the product. Adding a new variant (e.g. blackInverted) to an
 * existing family: add the key here; the resolver picks it up.
 *
 * @typedef {"ide"|"jb"|"jb-black"|"academy"|"ai"|"data"|"databao"|"dotnet"|"gamedev"|"ih"|"marketplace"|"as"|"youtrack"} LogoFamily
 */
const LOGO_FAMILIES = {
  ide: {
    normal: logoIDE,
    black: logoIDEBlack,
    white: logoIDEWhite,
    blackInverted: logoNormBlackInverted,
  },
  jb: {
    normal: logoNorm,
    black: logoNormBlack,
    white: logoWhite,
    blackInverted: logoNormBlackInverted,
  },
  // JetBrains Air: the banner design is a light canvas, so the wordmark is
  // black whatever the background says. Every variant resolves to the black
  // lockup on purpose — the marker-driven variants are how a product follows
  // its background, and Air does not: it is a one-background product whose
  // design fixes the contrast. It also has to hold on the saved-scene and
  // export path, where a catalog-only background carries no markers at all
  // (issue #698). Give Air a dark
  // background one day and this becomes the plain `jb` family again.
  "jb-black": {
    normal: logoNormBlack,
    black: logoNormBlack,
    white: logoNormBlack,
    blackInverted: logoNormBlack,
    inverted: logoNormBlack,
  },
  academy: {normal: logoAcademyNorm, black: logoAcademyBlack},
  dotnet: {normal: logoDotNETNorm, black: logoDotNETBlack},
  ai: {normal: logoAINorm, black: logoAIBlack},
  gamedev: {normal: logoGameDevNorm, black: logoGameDevBlack},
  marketplace: {normal: logoMarketplaceNorm, black: logoMarketplaceBlack},
  // YouTrack: black variant only applies on bgs flagged with both
  // `_blacklogo` AND `_productlogo`. Otherwise falls back to normal.
  youtrack: {normal: logoNorm, black: logoYouTrackBlack, _requiresProductLogo: true},
  ih: {normal: logoNormBlack, inverted: logoNormInverted},
  as: {normal: logoNorm, inverted: logoNormInverted},
  databao: {normal: logoDatabaoOutlined},
  data: {normal: logoDataNorm, black: logoDataBlack, white: logoDataWhite},
};

export const isBlackLogoUrl = (url) => {
  if (!url) return false;
  return url.includes("_black") || url.includes("black_logo") || url.includes("logo_black");
};

/**
 * Bg-marker → variant resolver. Pure function, exported for tests.
 * @param {Record<string, string> & {_requiresProductLogo?: boolean}} family
 * @param {readonly string[] | null | undefined} bgMarkers style markers of the
 *   background being rendered (libs/bgMarkers.ts).
 */
export function resolveVariant(family, bgMarkers) {
  const has = (marker) => Boolean(bgMarkers?.includes(marker));

  if (family._requiresProductLogo) {
    // YouTrack-style: the family's black variant is gated on the
    // blacklogo + productlogo combination. For any other bg shape,
    // the family doesn't claim the variant — defer to the jb family.
    if (has("blacklogo") && has("productlogo")) {
      return family.black ?? family.normal;
    }
    return resolveVariant(LOGO_FAMILIES.jb, bgMarkers);
  }
  // A `_blacklogo_inverted` background carries BOTH markers, so the qualified
  // one has to be checked first.
  if (has("blacklogoinverted")) {
    return family.blackInverted ?? family.black ?? family.normal;
  }
  if (has("blacklogo")) {
    return family.black ?? family.normal;
  }
  if (has("whitelogoinverted")) {
    return family.inverted ?? family.white ?? family.normal;
  }
  return family.normal;
}

/**
 * @param {string} productValue
 * @param {readonly string[] | null | undefined} bgMarkers
 * @param {string} [type]
 * @param {string} [channel]
 */
/** Channels where an IDE shows the plain JetBrains logo. */
const PLAIN_LOGO_CHANNELS = new Set(["social", "releases"]);

export function getLogoUrl(productValue, bgMarkers, type, channel) {
  // Channel-level overrides (not per-product)
  if (channel === "video") return logoMinimal;
  if (channel === "events") return logoMinimalColor;
  if (channel === "releases" && bgMarkers?.includes("whitelogotransparent")) {
    return logoWhiteTransparent;
  }

  const config = getProduct(productValue);
  if (!config) {
    throw new Error(
      `Unknown product "${productValue}". Add an entry in src/data/products/ and register it in src/data/products/index.js.`
    );
  }
  // The module-load invariant in src/data/products/index.js guarantees
  // logoFamily is a non-empty string for every registered product, so this
  // guard exists only as belt-and-suspenders against future regressions
  // in that invariant.
  // Social and Releases show the plain JetBrains logo, not the "JetBrains
  // IDEs" lockup (Sergey, 2026-10-23). Other channels keep the lockup.
  const familyName =
    config.logoFamily === "ide" && PLAIN_LOGO_CHANNELS.has(channel) ? "jb" : config.logoFamily;
  const family = LOGO_FAMILIES[familyName];
  if (!family) {
    throw new Error(
      `Product "${productValue}" has unknown logoFamily "${config.logoFamily}". ` +
        `Valid values: ${Object.keys(LOGO_FAMILIES).join(", ")}.`
    );
  }
  return resolveVariant(family, bgMarkers);
}

/**
 * Markers a generative gradient background stands in for: its vivid palette
 * colors want each family's own white/inverted logo, which is exactly the
 * `_whitelogo_inverted` case. Generative bgs are drawn client-side and have no
 * file, hence no markers of their own.
 */
export const GENERATIVE_BG_MARKERS = Object.freeze(["whitelogo", "whitelogoinverted"]);

export function getLogoCycle(productValue, type, layoutOption, channelValue) {
  let cycle;
  if (PRODUCT_LOGO_CYCLES[productValue]) {
    cycle = PRODUCT_LOGO_CYCLES[productValue];
  } else if (checkIfIDE(productValue, type)) {
    cycle = PRODUCT_LOGO_CYCLES._ide;
  } else {
    cycle = PRODUCT_LOGO_CYCLES._default;
  }

  if (
    productValue === "UNI" &&
    channelValue !== "livestream" &&
    ![5, 6].includes(Number(layoutOption))
  ) {
    return cycle.filter((url) => !url || !url.includes("_two"));
  }
  return cycle;
}
